import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useActivityLogger } from "@/hooks/useActivityLogger";
import { useAuth } from "@/context/AuthContext";
import { analytics } from "@/services/analytics";

/**
 * Tracks user activity and page views within the application.
 * @example
 * ActivityTracker()
 * // returns null
 * @returns {null} This component does not render anything.
 */
export const ActivityTracker = () => {
  const location = useLocation();
  const isDev = import.meta.env.DEV;

  const { user, loading: authLoading } = useAuth();

  // Initialize activity logger with conditional auth access
  useActivityLogger();

  // Track page views with session replay integration
  // Wait for auth to finish loading before tracking to avoid logging "anonymous"
  useEffect(() => {
    // Don't track if auth is still loading - wait for auth to finish
    if (authLoading) {
      return;
    }

    // Track page view in analytics
    analytics.trackPageView(location.pathname, {
      userId: user?.id,
      email: user?.email,
      isAuthenticated: !!user,
      timestamp: new Date().toISOString(),
    });

    // Avoid console noise in production
    if (isDev) {
      console.warn("Page visited:", {
        path: location.pathname,
        user: user?.email || user?.id || "anonymous",
        timestamp: new Date().toISOString(),
        isAuthenticated: !!user,
      });
    }
  }, [location.pathname, user, authLoading, isDev]);

  // Track user authentication state changes
  useEffect(() => {
    // Don't log if auth is still loading
    if (authLoading) {
      return;
    }

    if (user) {
      // User is authenticated - this will be handled by AuthContext
      if (isDev) {
        console.warn("User authenticated in ActivityTracker:", user.email || user.id);
      }
    } else if (isDev) {
      // User is anonymous (but only log this after loading is complete)
      console.warn("Anonymous user in ActivityTracker");
    }
  }, [user, authLoading, isDev]);

  // This component doesn't render anything
  return null;
};
